import { isChunkLoadError } from "@/lib/chunk-load-recovery";
import { recoverFromRetiredDeployment } from "@/lib/error-tracking/deployment-recovery";

interface PreloadErrorEventTarget {
  addEventListener(type: string, listener: (event: Event) => void): void;
}

const STYLESHEET_PRELOAD_FAILURE = /^unable to preload css for /i;

function toError(value: unknown): Error | null {
  if (value instanceof Error) return value;
  if (typeof value === "string") return new Error(value);
  return null;
}

function recoverIfChunkLoad(value: unknown): boolean {
  const error = toError(value);
  if (error === null || !isChunkLoadError(error)) return false;
  return recoverFromRetiredDeployment(error) === "reloading";
}

function isStylesheetPreloadFailure(value: unknown): boolean {
  const error = toError(value);
  return error !== null && STYLESHEET_PRELOAD_FAILURE.test(error.message);
}

export function installPreloadErrorRecovery(
  target: PreloadErrorEventTarget,
): void {
  target.addEventListener("vite:preloadError", (event) => {
    const payload = "payload" in event ? event.payload : undefined;
    if (isStylesheetPreloadFailure(payload)) {
      event.preventDefault();
      return;
    }
    recoverIfChunkLoad(payload);
  });
  target.addEventListener("unhandledrejection", (event) => {
    const reason = "reason" in event ? event.reason : undefined;
    if (recoverIfChunkLoad(reason)) event.preventDefault();
  });
  target.addEventListener("error", (event) => {
    const error = "error" in event ? event.error : undefined;
    if (recoverIfChunkLoad(error)) event.preventDefault();
  });
}
